/**
 * The small markdown every blog block field understands.
 *
 * The editor's body text is a real TipTap document, so a sentence in a
 * paragraph can carry any mark. The blocks are a different shape: a checklist
 * item, a table cell and a pull quote are stored as plain strings on the node's
 * attributes, which is what keeps a block one saveable unit and what lets the
 * article generator emit one. A string cannot carry a mark, so the emphasis
 * travels in the text itself and is parsed on the way out.
 *
 * Five things are understood, and nothing else:
 *
 *     [label](https://example.com)   a link
 *     **bold**                       bold
 *     *italic*                       italic
 *     ~~struck~~                     strikethrough
 *     `code`                         inline code
 *
 * There is no escape character. A delimiter that does not close, or closes
 * somewhere that would read as an accident (a space just inside it, a run of
 * asterisks in a price), stays literal, so ordinary prose cannot be turned into
 * emphasis by punctuation it happens to contain.
 *
 * Parsing lives apart from rendering because the same string is rendered three
 * ways: as React by `InlineMarkdown` on the site and in the editor's preview,
 * and as static HTML by each block's `renderHTML` for the `content_html`
 * snapshot RSS readers get.
 */

export type InlineMark = 'bold' | 'italic' | 'strike' | 'code';

export type InlineSegment =
  | { type: 'text'; value: string }
  | { type: 'mark'; mark: InlineMark; content: InlineSegment[] }
  | { type: 'link'; href: string; content: InlineSegment[] };

/** Longest delimiter first, so `**` is read as bold rather than two italics. */
const DELIMITERS: Array<{ token: string; mark: InlineMark }> = [
  { token: '**', mark: 'bold' },
  { token: '~~', mark: 'strike' },
  { token: '*', mark: 'italic' },
];

const LINK = /^\[([^\]]*)\]\(([^()\s]+)\)/;
const CODE = /^`([^`]+)`/;

/**
 * Only plain web links, our own paths, in-page anchors and mail addresses
 * become links. Anything else (`javascript:`, a bare word) stays literal
 * markdown so a field can't smuggle in a URL scheme we didn't intend.
 */
function isSafeHref(href: string): boolean {
  return (
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    href.startsWith('/') ||
    href.startsWith('#')
  );
}

/** An opening delimiter has to start a word, not sit inside one ("5*4*3"). */
function opensHere(input: string, at: number, token: string): boolean {
  const before = input[at - 1];
  const after = input[at + token.length];
  if (before !== undefined && /\w/.test(before)) return false;
  return after !== undefined && !/\s/.test(after);
}

/**
 * The matching close for a delimiter opened at `from`.
 *
 * A candidate is skipped when it is part of a longer run of the same character
 * (the `**` inside `*a **b** c*` closes the bold, not the italic) or when it
 * has a space in front of it, which is how "2 * 3 * 4" stays arithmetic.
 * Returns -1 when the delimiter never closes, and the run stays literal.
 */
function findClosing(input: string, from: number, token: string): number {
  const char = token[0];
  let cursor = from;

  while (cursor < input.length) {
    const found = input.indexOf(token, cursor);
    if (found < 0) return -1;

    const before = input[found - 1];
    const after = input[found + token.length];
    const inLongerRun = after === char || (found > from && before === char);

    if (!inLongerRun && before !== undefined && !/\s/.test(before)) return found;
    cursor = found + token.length;
  }

  return -1;
}

/** Split a field's text into its plain runs, its marks and its links. */
export function parseInline(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  let buffer = '';
  let i = 0;

  const flush = () => {
    if (buffer) segments.push({ type: 'text', value: buffer });
    buffer = '';
  };

  while (i < text.length) {
    const rest = text.slice(i);

    // Code first: what is inside it is literal, delimiters included.
    const code = CODE.exec(rest);
    if (code) {
      flush();
      segments.push({ type: 'mark', mark: 'code', content: [{ type: 'text', value: code[1] }] });
      i += code[0].length;
      continue;
    }

    const link = LINK.exec(rest);
    if (link) {
      const [raw, label, href] = link;
      if (isSafeHref(href)) {
        flush();
        segments.push({ type: 'link', href, content: parseInline(label) });
      } else {
        buffer += raw;
      }
      i += raw.length;
      continue;
    }

    const delimiter = DELIMITERS.find(
      ({ token }) => rest.startsWith(token) && opensHere(text, i, token),
    );
    if (delimiter) {
      const { token, mark } = delimiter;
      const closing = findClosing(rest, token.length, token);
      // Strictly greater, so an empty pair ("****") stays literal.
      if (closing > token.length) {
        flush();
        segments.push({
          type: 'mark',
          mark,
          content: parseInline(rest.slice(token.length, closing)),
        });
        i += closing + token.length;
        continue;
      }
    }

    buffer += text[i];
    i += 1;
  }

  flush();
  return segments;
}

/**
 * The field as it reads aloud, with the markup stripped, for the places that
 * can only hold a string (an iframe's `title`, say).
 */
export function inlineText(text: string): string {
  return inlineSegmentsText(parseInline(text));
}

function inlineSegmentsText(segments: InlineSegment[]): string {
  return segments
    .map((segment) => (segment.type === 'text' ? segment.value : inlineSegmentsText(segment.content)))
    .join('');
}

/** The HTML element each mark renders as, shared by the React and static paths. */
export const MARK_TAG: Record<InlineMark, 'strong' | 'em' | 's' | 'code'> = {
  bold: 'strong',
  italic: 'em',
  strike: 's',
  code: 'code',
};

/**
 * Whether a link should open away from the article. Our own paths and in-page
 * anchors stay in the tab; anything that leaves the site does not.
 */
export function opensInNewTab(href: string): boolean {
  return /^https?:\/\//i.test(href);
}
